import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { getAuthHeader, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [membership, setMembership] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/membership/status/${sessionId}`,
        getAuthHeader()
      );

      if (response.data.status === "paid") {
        setStatus("success");
        setMembership(response.data.membership);
        // Clear affiliate code after successful purchase
        localStorage.removeItem("advanta_affiliate");
        // Refresh user data
        refreshUser();
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Payment status check error:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <div className="animate-fade-in-up" data-testid="payment-checking">
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl text-white mb-4">
              Processing Payment
            </h1>
            <p className="text-white/50">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in-up" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-[#047857]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#047857]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl text-white mb-4">
              Payment Successful!
            </h1>
            <p className="text-white/50 mb-8">
              Your Advanta Plus membership is now active. Start exploring partner discounts today!
            </p>
            
            {membership && (
              <div className="bg-[#0B0F19] rounded-xl p-6 border border-[#D4AF37]/20 mb-8">
                <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2">Your Plan</p>
                <p className="text-white text-xl font-['Playfair_Display']">{membership.plan_name}</p>
                <p className="text-white/50 text-sm mt-2">Membership ID: {membership.id.slice(0, 8).toUpperCase()}</p>
              </div>
            )}
            
            <div className="flex flex-col gap-4">
              <Link to="/dashboard">
                <Button className="btn-gold w-full py-6 rounded text-base" data-testid="view-membership-btn">
                  View My Membership
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/businesses">
                <Button variant="ghost" className="btn-gold-outline w-full py-4 rounded" data-testid="browse-partners-btn">
                  Browse Partners
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="animate-fade-in-up" data-testid="payment-failed">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl text-white mb-4">
              Payment Issue
            </h1>
            <p className="text-white/50 mb-8">
              We couldn't confirm your payment. Please try again or contact support if the issue persists.
            </p>
            <div className="flex flex-col gap-4">
              <Link to="/membership">
                <Button className="btn-gold w-full py-6 rounded text-base" data-testid="try-again-btn">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="ghost" className="btn-gold-outline w-full py-4 rounded">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
